'use client'

import { useState, useCallback } from 'react'
import { AuthResult } from '@/types/api'

/**
 * Custom hook for managing form error states and messages
 */
export const useFormError = () => {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading)
  }, [])

  const handleAsyncOperation = useCallback(
    async <T>(
      operation: () => Promise<T>,
      errorMessage: string,
      onSuccess?: (result: T) => void,
      onError?: (error: unknown) => void
    ): Promise<T | null> => {
      setError(null)
      setIsLoading(true)

      try {
        const result = await operation()
        if (result && typeof result === 'object' && 'error' in result && 'success' in result) {
          const authResult = result as AuthResult
          if (!authResult.success) {
            setError(authResult.error || errorMessage)
            onError?.(authResult)
            return null
          }
        }

        onSuccess?.(result)
        return result
      } catch (err) {
        setError(errorMessage)
        onError?.(err)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  return {
    error,
    isLoading,
    clearError,
    setLoading,
    handleAsyncOperation,
  }
}
